import React, { createContext, useContext, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { GlobalState } from '../stores/reducer'
import { localStorageWrapper } from '../helpers/localStorage'
import { AppType, APP_TYPES, APP_CONFIGS, AppConfig } from '../types/appType'

interface AppTypeContextValue {
  appType: AppType
  config: AppConfig
  setAppType: (appType: AppType) => void
}

const AppTypeContext = createContext<AppTypeContextValue | undefined>(undefined)

// 旧アプリタイプから新アプリタイプへのマイグレーション
const migrateAppType = (stored: string | null): AppType => {
  if (!stored) return APP_TYPES.MEDILAB

  // 削除済みの旧アプリタイプをマイグレーション
  if (stored === 'Medilab_LATEST') return APP_TYPES.MEDILAB
  if (stored === 'TMN_BETA') return APP_TYPES.TMN

  // 有効なアプリタイプかチェック
  const validAppTypes = Object.values(APP_TYPES) as string[]
  if (validAppTypes.includes(stored)) {
    return stored as AppType
  }

  return APP_TYPES.MEDILAB
}

export const AppTypeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isLoggedIn = useSelector((state: GlobalState) => state.isLoggedIn)
  const [appType, setAppTypeState] = useState<AppType>(() => {
    const stored = localStorageWrapper.getItem('app_type')
    return migrateAppType(stored)
  })

  /**
   * set-state-in-effect の正当性:
   * - localStorage との同期は「外部ストレージ同期」であり、useEffect の本来の用途
   * - isLoggedIn が変化した時のみ実行され、カスケードレンダリングは発生しない
   * - 派生状態（props から計算可能な値）ではなく、外部ソースからの読み取り
   *
   * React Compiler の set-state-in-effect ルールは「派生状態の誤用」を防ぐためのもの。
   * 外部ストレージ同期は該当しない。
   * 参考: https://react.dev/learn/you-might-not-need-an-effect#subscribing-to-an-external-store
   */
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!isLoggedIn) return
    const stored = localStorageWrapper.getItem('app_type')
    if (stored) {
      setAppTypeState(migrateAppType(stored))
    }
  }, [isLoggedIn])
  /* eslint-enable react-hooks/set-state-in-effect */

  const setAppType = (newAppType: AppType) => {
    localStorageWrapper.setItem('app_type', newAppType)
    setAppTypeState(newAppType)
  }

  const config = APP_CONFIGS[appType]

  return (
    <AppTypeContext.Provider value={{ appType, config, setAppType }}>
      {children}
    </AppTypeContext.Provider>
  )
}

export const useAppType = () => {
  const context = useContext(AppTypeContext)
  if (!context) {
    throw new Error('useAppType must be used within AppTypeProvider')
  }
  return context
}
